import React, { useEffect, useState } from "react";
import { PlusCircle, X, AlertCircle, FolderOpen, Package } from "lucide-react";
import { Alert, Card, Checkbox, Divider } from "antd";
import { CircularProgress, Drawer, RadioGroup } from "@mui/material";
import DialogWrapper from "../../DialogWrapper";
import useDialogActionsHook from "../../CustomHooks.js/useDialogActionsHook";
import { callNonStoreApi } from "../../NonStoreApis";
import {
  GetProductsCategoryWise,
  UpdateDmAutomation,
} from "../../Api/ApiEndPoints";
import ProductCataloguePopup, { ProductCard } from "./ProductCataloguePopup";
import { AddFlex, CustomText, PrimaryActionButton } from "../../reusableStyles";
import { useLayout } from "../../CustomHooks.js/LayoutContext";
import useWindowSize from "../../Hooks/useWindowSize";
import { enqueueSnackbar } from "notistack";
import { useSelector } from "react-redux";

const InstaDmAutomation = ({ dmAutomation, setButton }) => {
  const { openDialog, isDialogOpen, closeDialog } = useDialogActionsHook();
  const { credentials } = useSelector((state) => state.instagram);
  const { setLayoutComponent, clearLayoutComponent } = useLayout();
  const { isMobileView } = useWindowSize();
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [keywords, setKeywords] = useState(dmAutomation?.keywords || [""]);
  const [selectionType, setSelectionType] = useState(null);
  const [icebreakers, setIcebreakers] = useState([""]);
  const [errors, setErrors] = useState({ keywords: "", icebreakers: "" });
  const [selectedProductsData, setSelectedProductsData] = useState(
    dmAutomation?.productData || {
      type: "products",
      data: [],
    }
  );

  const handleKeywordChange = (index, value) => {
    const newKeywords = [...keywords];
    newKeywords[index] = value;
    setKeywords((prev) =>
      prev.map((keyword, _index) => (_index === index ? value : keyword))
    );
    validateKeywords(newKeywords);
  };

  const handleIcebreakerChange = (index, value) => {
    const newIcebreakers = [...icebreakers];
    newIcebreakers[index] = value;
    setIcebreakers(newIcebreakers);
    validateIcebreakers(newIcebreakers);
  };

  const addKeyword = () => {
    if (keywords.length < 10) {
      setKeywords([...keywords, ""]);
    }
  };

  const addIcebreaker = () => {
    if (icebreakers.length < 5) {
      setIcebreakers([...icebreakers, ""]);
    }
  };

  const removeKeyword = (index) => {
    if (keywords.length > 1) {
      const newKeywords = keywords.filter((_, i) => i !== index);
      setKeywords(newKeywords);
      validateKeywords(newKeywords);
    }
  };

  const removeIcebreaker = (index) => {
    if (icebreakers.length > 1) {
      const newIcebreakers = icebreakers.filter((_, i) => i !== index);
      setIcebreakers(newIcebreakers);
      validateIcebreakers(newIcebreakers);
    }
  };

  const validateKeywords = (keywords) => {
    const newErrors = { ...errors };
    if (keywords.some((k) => k.length < 2)) {
      newErrors.keywords = "Keywords must be at least 2 characters long";
    } else if (keywords.some((k) => !k.trim())) {
      newErrors.keywords = "Keywords cannot be empty";
    } else if (
      new Set(keywords.map((k) => k.toLowerCase())).size !== keywords.length
    ) {
      newErrors.keywords = "Duplicate keywords are not allowed";
    } else {
      newErrors.keywords = "";
    }
    setErrors(newErrors);
  };

  const validateIcebreakers = (icebreakers) => {
    const newErrors = { ...errors };
    if (icebreakers.some((i) => i.length < 10)) {
      newErrors.icebreakers = "Icebreakers must be at least 10 characters long";
    } else if (icebreakers.some((i) => !i.trim())) {
      newErrors.icebreakers = "Icebreakers cannot be empty";
    } else if (
      new Set(icebreakers.map((i) => i.toLowerCase())).size !==
      icebreakers.length
    ) {
      newErrors.icebreakers = "Duplicate icebreakers are not allowed";
    } else {
      newErrors.icebreakers = "";
    }
    setErrors(newErrors);
  };

  const handleTypeChange = (product) => {
    setSelectionType(product);
  };

  const handleGetInitialData = async () => {
    // get products
    const body = {
      instaUserId: credentials.user_id,
      instaId: credentials.id,
    };
    const response = await callNonStoreApi(GetProductsCategoryWise, body);
    setProducts(response.data.products);
    setKeywords(dmAutomation?.keywords || [""]);
    setSelectedProductsData(
      dmAutomation?.productData || {
        type: "products",
        data: [],
      }
    );
  };

  const updateDmAutomation = async () => {
    try {
      setLoading(true);
      await callNonStoreApi(UpdateDmAutomation, {
        productData: selectedProductsData,
        keywords: keywords,
        instaUserId: credentials.user_id,
        instaId: credentials.id,
      });
      enqueueSnackbar("Updated automation", {
        variant: "success",
      });
    } catch (error) {
      enqueueSnackbar("Something went wrong, please try again later", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleGetInitialData();
  }, [dmAutomation, credentials]);

  useEffect(() => {
    setButton({
      Component: PrimaryActionButton,
      props: {
        onClick: () => {
          updateDmAutomation();
        },
        style: {
          width: "200px",
          height: "38px",
        },
      },
      children: [
        <>
          {loading ? (
            <CircularProgress size={16} sx={{ color: "white" }} />
          ) : (
            "Update Automation"
          )}
        </>,
      ],
    });
  }, [selectedProductsData, keywords, credentials]);

  return (
    <>
      <div className="max-w-2xl mt-5">
        <div className="space-y-6">
          {/* <h2 className="text-2xl font-bold text-gray-900">
          DM Automation Setup
        </h2> */}

          {/* Keywords Section */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-700">
                Trigger Keywords
              </h3>
              <button
                onClick={addKeyword}
                disabled={keywords.length >= 10}
                className="flex items-center text-blue-600 hover:text-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <PlusCircle className="w-4 h-4 mr-1" />
                Add Keyword
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {keywords.map((keyword, index) => (
                <div
                  key={index}
                  className="flex"
                  style={{ marginRight: "10px" }}
                >
                  <input
                    type="text"
                    value={keyword}
                    onChange={(e) => handleKeywordChange(index, e.target.value)}
                    placeholder="Enter trigger keyword"
                    className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  {keywords.length > 1 && (
                    <button
                      onClick={() => removeKeyword(index)}
                      className="p-2 text-gray-500 hover:text-red-500"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {errors.keywords && <>{errors.keywords}</>}

            <p className="text-sm text-gray-500">
              Add up to 10 keywords that will trigger your automated responses
            </p>
            {/* <div className="flex items-center text-sm gap-2">
              <Checkbox />
              <AddFlex>Any Keyword</AddFlex>
            </div> */}
          </div>

          <div className="space-y-4" onClick={openDialog}>
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-700">
                Add Products
              </h3>
            </div>
            <div className="flex flex-col gap-2">
              <div
                className={`flex items-center space-x-2 p-4 border rounded-lg cursor-pointer `}
              >
                <input
                  type="radio"
                  value="product"
                  checked={selectionType === "product"}
                  onChange={() => handleTypeChange("product")}
                  className="sr-only"
                />
                <Package className="w-5 h-5" />
                <div>
                  <p className="font-medium">Select Products</p>
                  <p className="text-sm text-gray-500">
                    Add upto 25 individual products
                  </p>
                </div>
              </div>
              <AddFlex
                margin="0 0 30vh 0"
                className="flex flex-col gap-2"
                wrap="wrap"
              >
                {selectedProductsData.type === "products" ? (
                  selectedProductsData.data.map((product) => (
                    <ProductCard product={product} disabled={true} />
                  ))
                ) : (
                  <AddFlex gap="10px" flexDirection="column" width="100%">
                    {Object.entries(selectedProductsData.data).map(
                      ([key, value]) => (
                        <>
                          <AddFlex gap="5px" flexDirection="column">
                            <CustomText
                              color="black"
                              fontSize="18px"
                              fontWeight="600"
                              margin="0 0 10px 0"
                            >
                              {" "}
                              {key}
                            </CustomText>
                            <AddFlex className="gap-2" wrap="wrap">
                              {value.data.map((product) => (
                                <ProductCard
                                  product={product}
                                  disabled={true}
                                />
                              ))}
                            </AddFlex>
                          </AddFlex>
                          <Divider />
                        </>
                      )
                    )}
                  </AddFlex>
                )}
              </AddFlex>
            </div>
          </div>
        </div>
      </div>
      {isMobileView() && (
        <AddFlex
          padding="20px"
          backgroundColor="white"
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            width: "100vw",
            boxShadow: "0 0 4px 2px rgba(0,0,0,0.1)",
          }}
        >
          <PrimaryActionButton
            onClick={() => {
              updateDmAutomation();
            }}
            style={{ margin: "auto", width: "70%", height: "40px" }}
          >
            {loading ? (
              <CircularProgress size={16} sx={{ color: "white" }} />
            ) : (
              "Update Dm Automation"
            )}
          </PrimaryActionButton>
        </AddFlex>
      )}
      <Drawer
        open={isDialogOpen}
        anchor="right"
        onClose={closeDialog}
        PaperProps={{
          sx: {
            width: isMobileView() ? "100vw" : "60vw",
          },
        }}
      >
        <ProductCataloguePopup
          products={products}
          setSelectedProducts={setSelectedProductsData}
          selectedProducts={selectedProductsData}
          onClose={closeDialog}
        />
      </Drawer>
    </>
  );
};

export default InstaDmAutomation;
